import { ChangeEvent, Fragment, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { createTheme, ThemeProvider } from "@mui/material"
import { isEqual, isUndefined } from "lodash"
import classNames from "classnames"

import ProfileUploader from "@/components/profileUploader/ProfileUploader"
import TextField from "@/components/form/textField/TextField"
import Tab from "@/components/tab/Tab"
import BasicButton from "@/components/button/BasicButton"
import Selector from "@/components/form/selector/Selector"
import { useAssignableDevicesQuery, useDevicesQuery } from "@/hooks/api/useDeviceQuery"
import {
  usePlayerInfoQuery,
  useUpsertPlayerMutation,
  useUpsertPlayerWithDeviceMutation,
} from "@/hooks/api/usePlayerQuery"
import { usePositionsQuery } from "@/hooks/api/usePositionQuery"
import {
  useCreateProfileImageMutation,
  useDeleteProfileImageMutation,
} from "@/hooks/api/useAccountQuery"
import { useGetInfoErrMsg } from "@/app/settings/club/players/hooks/useGetInfoErrMsg/useGetInfoErrMsg"
import { useGetInfoIsErr } from "@/app/settings/club/players/hooks/useGetInfoIsErr/useGetInfoIsErr"
import { MainFooted, TeamPlayer } from "@/types/player"
import { createGlobalStore } from "@/stores/global"
import { createAccountStore } from "@/stores/account"
import { showDominantHand } from "@/constants/sportConfig"
import { getImagePath } from "@/utils/image"
import { PositionType } from "@/types/position"

import DatePicker from "@/components/form/datePicker/DatePicker"
import moment from "moment"
import "./playerInfo.scss"
import NumericStepper from "@/components/form/numericStepper/NumericStepper"

const theme = createTheme({
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          "--Paper-shadow":
            "0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12) !important;", // 전역 Paper shadow
          "& .MuiAutocomplete-listbox": {
            maxHeight: "304px",
          },
        },
      },
    },
  },
})

export default function PlayerInfo({
  idfPlayer,
  openRemoveDialog,
  refetch,
}: {
  idfPlayer: number | undefined
  openRemoveDialog: () => void
  refetch?: () => void
}) {
  const { t } = useTranslation()
  const { data: player, refetch: refetchPlayer } = usePlayerInfoQuery({ idfPlayer })
  const { data: assignableDevices } = useAssignableDevicesQuery(idfPlayer)
  const { data: devices } = useDevicesQuery()
  // 포지션 드롭다운은 groupBy로 그룹 헤더를 그리므로 그룹 클러스터 순서(ordering=group)로 조회.
  const { data: positions } = usePositionsQuery("group")

  const { mutate } = useUpsertPlayerWithDeviceMutation({ idfPlayer })
  const { mutateAsync: createProfileImageMutate } = useCreateProfileImageMutation()
  const { mutateAsync: deleteProfileImageMutate } = useDeleteProfileImageMutation()

  const [newPlayer, setNewPlayer] = useState<TeamPlayer | undefined>(player)
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)
  // 종목별: 럭비는 주발+주손, 그 외는 주발만 (sportConfig.showDominantHand)
  const sportType = createAccountStore((state) => state.teamProfileInfo?.sportType)

  useEffect(() => {
    if (player) {
      setNewPlayer(player)
    }
  }, [player])

  // 주발/주손 옵션 (값은 MainFooted: L/R/A, A=Both). 한국어는 손/발 표기가 달라 필드별 키를 쓴다.
  const footOptions = [
    { label: t("leftFoot", "Left"), value: "L" },
    { label: t("rightFoot", "Right"), value: "R" },
    { label: t("bothFeet", "Both"), value: "A" },
  ]
  const handOptions = [
    { label: t("leftHand", "Left"), value: "L" },
    { label: t("rightHand", "Right"), value: "R" },
    { label: t("bothHands", "Both"), value: "A" },
  ]

  const renderFootHandField = (field: "foot" | "hand", label: string) => (
    <div className="player-info-field">
      <p>{label}</p>
      <Tab
        type="filled"
        list={field === "hand" ? handOptions : footOptions}
        value={newPlayer?.[field] ?? ""}
        onChange={(_, value) => idfPlayer && onChangeField(field, String(value))}
      />
    </div>
  )

  const assignedDeviceValue = useMemo(() => {
    return (
      assignableDevices?.find((device) => device.serialNumber === newPlayer?.deviceSerial)
        ?.serialNumber ?? t("none")
    )
  }, [assignableDevices, newPlayer?.deviceSerial])

  const positionOptions = useMemo(() => {
    // 그룹 클러스터 순서(ordering=group)로 받으므로 Selector groupBy가 그대로 묶임(재정렬 불필요).
    return (
      positions?.map((position) => ({
        id: position.code,
        label: position.code,
        ...position,
      })) ?? []
    )
  }, [positions])

  const onChangeField = (key: keyof TeamPlayer, value: string | number | MainFooted) => {
    setNewPlayer((prev) => {
      if (!prev) {
        return
      }

      return { ...prev, [key]: value }
    })
  }

  const onSave = async () => {
    if (!newPlayer || !idfPlayer) {
      return
    }
    try {
      const parts = newPlayer.deviceSerial?.split("-") || []
      const [model = "", version = "", number = 0] = parts
      mutate({
        idfPlayer,
        payload: {
          ...newPlayer,
          model,
          version,
          number: number ? Number(number) : 0,
        },
      })
      setSnackBar({
        message: t("messages.successUpdate"),
        severity: "success",
        open: true,
      })
    } catch (e) {
      setSnackBar({ message: t(String(e)), severity: "error", open: true })
    }
  }

  const onSaveImage = async (image?: string | Blob) => {
    if (!player?.idfPlayer) {
      return
    }

    if (image) {
      const formData = new FormData()
      const idfPlayer = String(player.idfPlayer)

      formData.append("idfAccount", idfPlayer)
      formData.append("fileImage", image)

      const res = await createProfileImageMutate(formData)

      if (res.response) {
        onChangeField("image", res.response.image)
        refetch?.() // 테이블 갱신
        refetchPlayer() // 상세정보 갱신
        setSnackBar({ message: t("messages.successUpdate"), severity: "success", open: true })
      }
    }
  }

  const onRemoveImage = async () => {
    if (!player?.idfPlayer) {
      return
    }

    const res = await deleteProfileImageMutate(String(player.idfPlayer))

    if (res.success) {
      onChangeField("image", "")
      refetch?.() // 테이블 갱신
      refetchPlayer() // 상세정보 갱신
      setSnackBar({ message: t("messages.successDelete"), severity: "success", open: true })
    }
  }

  const isErrSaveBtn = useGetInfoIsErr(newPlayer)

  const isDisabledSaveBtn = useMemo(() => {
    return isEqual(player, newPlayer) || isErrSaveBtn || !idfPlayer || !player || !newPlayer
  }, [player, newPlayer, idfPlayer])

  const isErrHeightWeight = (value?: number) => {
    if (isUndefined(value)) {
      return false
    } else {
      return value < 0
    }
  }

  const isErrJerseyNumber = (value?: number) => {
    return (typeof value !== "number" && !value) || value > 100 || value < 0
  }

  return (
    <div className={classNames("PlayerInfoCont", !idfPlayer ? "no-player" : "")}>
      <ProfileUploader
        imgUrl={newPlayer?.image ? getImagePath(newPlayer?.image) : ""}
        width={72}
        height={72}
        onSave={onSaveImage}
        onRemove={onRemoveImage}
      />
      <div className="player-info-field">
        <p>{t("name")}</p>
        <TextField
          value={newPlayer?.nickname ?? ""}
          onChange={(e) =>
            onChangeField("nickname", (e as ChangeEvent<HTMLInputElement>).target.value)
          }
          error={!newPlayer?.nickname || newPlayer.nickname.length > 35}
          showMessage={!newPlayer?.nickname || newPlayer.nickname.length > 35}
          message={useGetInfoErrMsg("nickname", t, newPlayer)}
          slotProps={{
            input: {
              readOnly: !idfPlayer,
            },
          }}
        />
      </div>
      <div className="player-info-field">
        <p>{t("birth")}</p>
        <DatePicker
          value={newPlayer?.birthday ? new Date(newPlayer.birthday) : null}
          placeholder="0000-00-00"
          defaultValueNull={true}
          maxDate={new Date()}
          onChange={(e) => {
            if (e) {
              onChangeField("birthday", moment(e).format("YYYY-MM-DD"))
            }
          }}
          disabled={!idfPlayer}
        />
      </div>
      <div className="player-info-row">
        <div className="player-info-field">
          <p>{t("jersey")}</p>
          <TextField
            value={newPlayer?.uniformNumber ?? 0}
            onChange={(e) =>
              onChangeField("uniformNumber", (e as ChangeEvent<HTMLInputElement>).target.value)
            }
            type="number"
            error={isErrJerseyNumber(newPlayer?.uniformNumber)}
            showMessage={isErrJerseyNumber(newPlayer?.uniformNumber)}
            message={useGetInfoErrMsg("uniformNumber", t, newPlayer)}
            slotProps={{
              input: {
                readOnly: !idfPlayer,
              },
            }}
          />
        </div>
        <div className="player-info-field">
          <p>{t("position")}</p>
          <ThemeProvider theme={theme}>
            <Selector
              options={positionOptions}
              onChange={(_: unknown, newVal: PositionType) =>
                onChangeField("position", newVal.code)
              }
              value={newPlayer?.position ?? ""}
              groupBy={(option) => option.group}
              renderGroup={(params) => {
                return (
                  <Fragment key={params.key}>
                    <div
                      className={classNames(
                        "player-info-position-selector",
                        Number(params.key) !== 0 ? "group-border-top" : "",
                      )}>
                      <span className="player-info-position-group">{params.group}</span>
                    </div>
                    {params.children}
                  </Fragment>
                )
              }}
              renderOption={(value) => {
                const { key, ...optionProps } = value

                return (
                  <li {...optionProps} className="player-info-position-option" key={key}>
                    <span className="player-info-position-value">{value.key}</span>
                  </li>
                )
              }}
              disabled={!idfPlayer}
            />
          </ThemeProvider>
        </div>
      </div>
      <div className="player-info-row">
        <div className="player-info-field">
          <p>{t("height")} (cm)</p>
          <NumericStepper
            value={newPlayer?.height ?? 0}
            onChange={(value) => onChangeField("height", value as number)}
            min={0}
            max={999}
            allowNegative={false}
            decimalScale={0}
            error={isErrHeightWeight(newPlayer?.height)}
            showMessage={isErrHeightWeight(newPlayer?.height)}
            message={useGetInfoErrMsg("height", t, newPlayer)}
            disabled={!idfPlayer}
          />
        </div>
        <div className="player-info-field">
          <p>{t("weight")} (kg)</p>
          <NumericStepper
            value={newPlayer?.weight ?? 0}
            onChange={(value) => onChangeField("weight", value as number)}
            min={0}
            max={999}
            allowNegative={false}
            decimalScale={0}
            error={isErrHeightWeight(newPlayer?.weight)}
            showMessage={isErrHeightWeight(newPlayer?.weight)}
            message={useGetInfoErrMsg("weight", t, newPlayer)}
            disabled={!idfPlayer}
          />
        </div>
      </div>
      <div className="player-info-row">
        {showDominantHand(sportType) ? (
          <>
            {renderFootHandField("hand", t("hand"))}
            {renderFootHandField("foot", t("foot"))}
          </>
        ) : (
          <>
            {renderFootHandField("foot", t("foot"))}
            <div className="player-info-field" aria-hidden />
          </>
        )}
      </div>
      <div className="player-info-field">
        <p>{t("assignedDevice")}</p>
        <ThemeProvider theme={theme}>
          <Selector
            options={[
              {
                id: "",
                label: t("none"),
              },
              ...(assignableDevices?.map((device) => ({
                id: device.serialNumber,
                label: device.serialNumber,
              })) ?? []),
            ]}
            onChange={(_: unknown, newVal: { id: string; label: string }) =>
              onChangeField("deviceSerial", newVal.id)
            }
            renderOption={(value) => {
              const { key, ...optionProps } = value

              return (
                <li {...optionProps} className="player-info-device-option" key={key}>
                  <span className="player-info-device-value">{value.key}</span>
                </li>
              )
            }}
            value={assignedDeviceValue}
            disabled={!devices?.length || !idfPlayer} // TODO 기기를 보유하지 않은 팀인 경우 비활성화
          />
        </ThemeProvider>
      </div>
      <div className="player-info-field">
        <p>{t("email")}</p>
        <TextField
          value={newPlayer?.email ?? ""}
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
        />
      </div>

      <div className="player-info-buttons">
        <BasicButton
          type="danger-outlined"
          size="md"
          onClick={openRemoveDialog}
          disabled={!idfPlayer}>
          {t("button.remove")}
        </BasicButton>
        <BasicButton
          type="primary-contained"
          size="md"
          onClick={onSave}
          disabled={isDisabledSaveBtn}>
          {t("button.save")}
        </BasicButton>
      </div>
    </div>
  )
}
